/**
 * Modulr lifecycle helper for block view scripts.
 *
 * `ready(init, { scope })` runs `init(root)` on DOMContentLoaded (or immediately
 * if the document is already parsed) AND on every `modulr:navigated` event
 * dispatched by the soft-navigation router.
 *
 * `init` may return a `destroy()` function. The previous teardown for the same
 * scope is invoked before the next init, so re-entry is safe even when the
 * router hot-swaps `<main>`.
 *
 * Behaviorally inert when soft-nav is disabled: with no router loaded, the
 * `modulr:navigated` event is never dispatched, so init runs exactly once on
 * DOMContentLoaded just like the legacy code path it replaces.
 *
 * Block view scripts import this module and wp-scripts bundles a private copy
 * into each block. State (teardowns) is module-scoped per block bundle, which
 * is desirable: each block manages only its own lifecycle.
 *
 * The IIFE twin at src/js/modulr-ready.js exposes the same API on
 * window.ModulrReady for gulp-built scripts that cannot use ES imports.
 */

export const MODULR_NAV_EVENT = 'modulr:navigated';

const teardowns = new Map();
let scopeCounter = 0;

function safeCall(fn, label) {
  try {
    return fn();
  } catch (err) {
    if (typeof console !== 'undefined' && console.error) {
      console.error('[modulr:ready] ' + label + ' threw', err);
    }
    return undefined;
  }
}

function runInit(scope, init, detail) {
  const previous = teardowns.get(scope);
  if (typeof previous === 'function') {
    safeCall(previous, 'destroy(' + scope + ')');
    teardowns.delete(scope);
  }
  const root = (detail && detail.root) || document;
  const result = safeCall(function () { return init(root, detail || {}); }, 'init(' + scope + ')');
  if (typeof result === 'function') {
    teardowns.set(scope, result);
  }
}

/**
 * Register an idempotent initializer that runs on initial load and on every
 * soft-navigation. The init may return a destroy function that will be called
 * before each subsequent re-init.
 *
 * @param {(root: ParentNode, detail: object) => (void | (() => void))} init
 * @param {{ scope?: string }} [options]
 */
export function ready(init, options) {
  const opts = options || {};
  const scope = opts.scope || init.name || ('modulr-anon-' + (++scopeCounter));

  function onNavigated(event) {
    runInit(scope, init, event && event.detail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      runInit(scope, init);
    }, { once: true });
  } else {
    runInit(scope, init);
  }

  window.addEventListener(MODULR_NAV_EVENT, onNavigated);
}

/**
 * Per-element idempotency guard. Returns true the first time it is called for
 * a given (element, key) pair, false thereafter. Prevents double-bind when
 * elements survive a partial DOM swap.
 *
 * @param {Element} el
 * @param {string} key
 * @returns {boolean}
 */
export function onceInitialized(el, key) {
  if (!el || !el.dataset) return false;
  const current = el.dataset.modulrInit || '';
  const keys = current ? current.split(' ') : [];
  if (keys.indexOf(key) !== -1) return false;
  keys.push(key);
  el.dataset.modulrInit = keys.join(' ');
  return true;
}

/**
 * Companion to `onceInitialized` — clears the guard so the element can be
 * re-initialized (e.g. inside a destroy() handler before swap).
 *
 * @param {Element} el
 * @param {string} key
 */
export function clearInitialized(el, key) {
  if (!el || !el.dataset) return;
  const current = el.dataset.modulrInit || '';
  if (!current) return;
  const next = current.split(' ').filter(function (k) { return k && k !== key; });
  if (next.length) {
    el.dataset.modulrInit = next.join(' ');
  } else {
    delete el.dataset.modulrInit;
  }
}
